import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { SPORTS_EVENTS, MY_SPORTS_REGISTRATIONS, CREATE_SPORTS_EVENT, REGISTER_FOR_SPORTS_EVENT } from '../graphql';
import { Trophy, Plus, MapPin, Calendar, Users, X, Filter, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SPORTS_LIST = ['Cricket','Football','Basketball','Badminton','Table Tennis','Chess','Carrom','Volleyball','Athletics','Swimming','Kabaddi','Kho-Kho','Tennis','Throwball','Cycling','E-Sports','Yoga','Martial Arts','Other'];
const STATUS_COLORS = { OPEN:'bg-green-100 text-green-700', CLOSED:'bg-gray-100 text-gray-500', COMPLETED:'bg-blue-100 text-blue-700' };

function EventCard({ event, onRegister, currentUserId }) {
  const [teamName, setTeamName] = useState('');
  const [showTeam, setShowTeam] = useState(false);
  const isRegistered = event.participants?.some(p => p.id === currentUserId);
  const isOrganizer = event.organizer?.id === currentUserId;
  const full = event.maxParticipants > 0 && event.participantCount >= event.maxParticipants;

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-bold text-gray-900">{event.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[event.status] || 'bg-gray-100 text-gray-500'}`}>{event.status}</span>
          </div>
          <p className="text-sm text-indigo-600 font-medium mb-1">{event.sport}</p>
          {event.description && <p className="text-sm text-gray-500 mb-2">{event.description}</p>}
          <div className="flex flex-wrap gap-3 text-xs text-gray-400">
            {event.eventDate && <span className="flex items-center gap-1"><Calendar size={11}/>{new Date(event.eventDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>}
            {event.venue && <span className="flex items-center gap-1"><MapPin size={11}/>{event.venue}</span>}
            <span className="flex items-center gap-1"><Users size={11}/>{event.participantCount}{event.maxParticipants > 0 ? `/${event.maxParticipants}` : ''} registered</span>
            {event.college && <span>{event.college}</span>}
          </div>
          {event.result && (
            <div className="mt-2 bg-yellow-50 rounded-lg px-3 py-1.5">
              <p className="text-xs font-semibold text-yellow-700">Result: {event.result}</p>
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-gray-400 mb-1">by {event.organizer?.name}</p>
          {!isOrganizer && event.status === 'OPEN' && !isRegistered && !full && (
            showTeam
              ? <div className="flex flex-col gap-1">
                  <input className="border border-gray-200 rounded-lg px-2 py-1 text-xs w-32" value={teamName} onChange={e=>setTeamName(e.target.value)} placeholder="Team name (opt.)" />
                  <div className="flex gap-1">
                    <button onClick={() => { onRegister(event.id, teamName); setShowTeam(false); }} className="btn-primary text-xs px-2 py-1">Join</button>
                    <button onClick={() => setShowTeam(false)} className="btn-secondary text-xs px-2 py-1">✕</button>
                  </div>
                </div>
              : <button onClick={() => setShowTeam(true)} className="btn-primary text-xs px-3 py-1.5">Register</button>
          )}
          {isRegistered && <span className="text-xs text-green-600 font-semibold">✓ Registered</span>}
          {full && !isRegistered && <span className="text-xs text-red-500 font-medium">Full</span>}
        </div>
      </div>

      {/* Participants preview */}
      {event.participants?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-50">
          <p className="text-xs text-gray-400 mb-1.5">Participants:</p>
          <div className="flex flex-wrap gap-1">
            {event.participants.slice(0, 8).map(p => (
              <span key={p.id} className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full">{p.name}</span>
            ))}
            {event.participants.length > 8 && <span className="text-xs text-gray-400">+{event.participants.length - 8} more</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function getSportEmoji(sport) {
  const map = { Cricket:'🏏', Football:'⚽', Basketball:'🏀', Badminton:'🏸', 'Table Tennis':'🏓', Chess:'♟️', Carrom:'🎯', Volleyball:'🏐', Athletics:'🏃', Swimming:'🏊', Kabaddi:'🤼', Tennis:'🎾', 'E-Sports':'🎮', Yoga:'🧘', 'Martial Arts':'🥋' };
  return map[sport] || '🏅';
}

export default function SportsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('all');
  const [sport, setSport] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', sport: 'Cricket', description: '', eventDate: '', venue: '', maxParticipants: '', registrationDeadline: '', college: '' });

  const { data, refetch } = useQuery(SPORTS_EVENTS, { variables: { sport: sport || undefined } });
  const { data: myData } = useQuery(MY_SPORTS_REGISTRATIONS);
  const [createEvent, { loading: creating }] = useMutation(CREATE_SPORTS_EVENT, { onCompleted: () => { refetch(); setShowCreate(false); }});
  const [register] = useMutation(REGISTER_FOR_SPORTS_EVENT, { onCompleted: () => refetch() });

  const events = data?.sportsEvents || [];
  const myEvents = myData?.mySportsRegistrations || [];

  const displayEvents = tab === 'mine' ? myEvents : tab === 'open' ? events.filter(e => e.status === 'OPEN') : events;

  const handleCreate = async (e) => {
    e.preventDefault();
    await createEvent({ variables: { ...form, maxParticipants: parseInt(form.maxParticipants)||0, eventDate: form.eventDate||null, registrationDeadline: form.registrationDeadline||null, college: form.college||null } });
    setForm({ title: '', sport: 'Cricket', description: '', eventDate: '', venue: '', maxParticipants: '', registrationDeadline: '', college: '' });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-xl"><Trophy size={20} className="text-orange-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sports Hub</h1>
            <p className="text-gray-500 text-sm">Campus sports competitions · Register by hobby · Find your game</p>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> Create Event</button>
      </div>

      {/* Tabs & Filters */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {[['all','All Events'],['open','Open'],['mine','My Events']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab===t?'bg-white shadow text-gray-900':'text-gray-500'}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400"/>
          <select className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none" value={sport} onChange={e => { setSport(e.target.value); refetch({ sport: e.target.value || undefined }); }}>
            <option value="">All Sports</option>
            {SPORTS_LIST.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[['Total Events', events.length, 'bg-orange-50 text-orange-700'],['Open Now', events.filter(e=>e.status==='OPEN').length, 'bg-green-50 text-green-700'],['Joined', myEvents.length, 'bg-indigo-50 text-indigo-700']].map(([l,v,c]) => (
          <div key={l} className={`${c} rounded-xl p-3 text-center`}>
            <p className="text-xl font-bold">{v}</p>
            <p className="text-xs font-medium mt-0.5">{l}</p>
          </div>
        ))}
      </div>

      {/* Events list */}
      {displayEvents.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-3">🏅</div>
          <p className="text-gray-500 font-medium">No events yet</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Create First Event</button>
        </div>
      ) : (
        <div className="space-y-3">
          {displayEvents.map(ev => <EventCard key={ev.id} event={ev} currentUserId={user?.id} onRegister={(id, team) => register({ variables: { eventId: id, teamName: team || undefined } })} />)}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Create Sports Event</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <input required className="input w-full" placeholder="Event title" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Sport</label>
                  <select className="input w-full" value={form.sport} onChange={e => setForm(f=>({...f,sport:e.target.value}))}>
                    {SPORTS_LIST.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Max Participants (0=unlimited)</label>
                  <input className="input w-full" type="number" min="0" value={form.maxParticipants} onChange={e => setForm(f=>({...f,maxParticipants:e.target.value}))} placeholder="0" />
                </div>
              </div>
              <textarea className="input w-full resize-none h-16 text-sm" placeholder="Description" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Event Date</label><input type="date" className="input w-full" value={form.eventDate} onChange={e => setForm(f=>({...f,eventDate:e.target.value}))} /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Reg. Deadline</label><input type="date" className="input w-full" value={form.registrationDeadline} onChange={e => setForm(f=>({...f,registrationDeadline:e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="input w-full" placeholder="Venue / Location" value={form.venue} onChange={e => setForm(f=>({...f,venue:e.target.value}))} />
                <input className="input w-full" placeholder="College (blank=all)" value={form.college} onChange={e => setForm(f=>({...f,college:e.target.value}))} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Creating…' : 'Create Event'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
